export interface Country {
  code_iso2: string;
  title: string;
  dial?: string;
  currencySymbol?: string;
  currencyCode?: string;
  isEU?: boolean;
}

export interface State {
  name: string;
  code: string;
}

export const getLocaleDataByCode = function (code?: string): Country | undefined {
  if (!code) {
    return undefined;
  }
  const iso = code.toUpperCase();
  return countries.filter(c => c.code_iso2 === iso)[0];
};

export const phoneNumberToCountry = function (phone: string, code?: string): Country | undefined {
  if (!phone) {
    return undefined;
  }
  let country = countries.find(c => {
    if (code) {
      return c.dial === `+${code}`.replace('++', '+');
    } else {
      return `+${phone}`.replace('++', '+').startsWith(c.dial || '+1');
    }
  });
  // US and Canada have same dialCode, so we additionally check areaCodes
  if (
    country?.code_iso2 === 'US' &&
    canadaStates.filter(s => s.areaCodes.some(code => phone.startsWith(`${code}`))).length
  ) {
    country = countries.filter(c => c.code_iso2 === 'CA')[0];
  }
  return country;
};

export const countries: Country[] = [
  {
    dial: '+355',
    code_iso2: 'AL',
    title: 'Albania',
    currencySymbol: 'L',
    currencyCode: 'LEK',
  },
  {
    dial: '+880',
    code_iso2: 'BD',
    title: 'Bangladesh',
    currencySymbol: 'Tk',
    currencyCode: 'BDT',
  },
  {
    dial: '+32',
    code_iso2: 'BE',
    title: 'Belgium',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+226',
    code_iso2: 'BF',
    title: 'Burkina Faso',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+359',
    code_iso2: 'BG',
    title: 'Bulgaria',
    currencySymbol: 'BGN',
    currencyCode: 'BGN',
    isEU: true,
  },
  {
    dial: '+387',
    code_iso2: 'BA',
    title: 'Bosnia and Herzegovina',
    currencySymbol: 'KM',
    currencyCode: 'BAM',
  },
  {
    dial: '+1246',
    code_iso2: 'BB',
    title: 'Barbados',
    currencySymbol: 'BBD',
    currencyCode: 'BBD',
  },
  {
    dial: '+590',
    code_iso2: 'BL',
    title: 'Saint Barthelemy',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+1441',
    code_iso2: 'BM',
    title: 'Bermuda',
    currencySymbol: 'BMD',
    currencyCode: 'BMD',
  },
  {
    dial: '+673',
    code_iso2: 'BN',
    title: 'Brunei',
    currencySymbol: 'BN$',
    currencyCode: 'BND',
  },
  {
    dial: '+591',
    code_iso2: 'BO',
    title: 'Bolivia',
    currencySymbol: 'Bs',
    currencyCode: 'BOB',
  },
  {
    dial: '+973',
    code_iso2: 'BH',
    title: 'Bahrain',
    currencySymbol: 'BD',
    currencyCode: 'BHD',
  },
  {
    dial: '+257',
    code_iso2: 'BI',
    title: 'Burundi',
    currencySymbol: 'FBu',
    currencyCode: 'BIF',
  },
  {
    dial: '+229',
    code_iso2: 'BJ',
    title: 'Benin',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+975',
    code_iso2: 'BT',
    title: 'Bhutan',
    currencySymbol: 'BTN',
    currencyCode: 'BTN',
  },
  {
    dial: '+1876',
    code_iso2: 'JM',
    title: 'Jamaica',
    currencySymbol: 'J$',
    currencyCode: 'JMD',
  },
  {
    dial: '+267',
    code_iso2: 'BW',
    title: 'Botswana',
    currencySymbol: 'BWP',
    currencyCode: 'BWP',
  },
  {
    dial: '+685',
    code_iso2: 'WS',
    title: 'Samoa',
    currencySymbol: 'WST',
    currencyCode: 'WST',
  },
  {
    dial: '+599',
    code_iso2: 'BQ',
    title: 'Bonaire, Saint Eustatius and Saba',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+55',
    code_iso2: 'BR',
    title: 'Brazil',
    currencySymbol: 'R$',
    currencyCode: 'BRL',
  },
  {
    dial: '+1242',
    code_iso2: 'BS',
    title: 'Bahamas',
    currencySymbol: 'BSD',
    currencyCode: 'BSD',
  },
  {
    dial: '+441534',
    code_iso2: 'JE',
    title: 'Jersey',
    currencySymbol: '£',
    currencyCode: 'GBP',
  },
  {
    dial: '+375',
    code_iso2: 'BY',
    title: 'Belarus',
    currencySymbol: 'BYN',
    currencyCode: 'BYN',
  },
  {
    dial: '+501',
    code_iso2: 'BZ',
    title: 'Belize',
    currencySymbol: 'BZ$',
    currencyCode: 'BZD',
  },
  {
    dial: '+7',
    code_iso2: 'RU',
    title: 'Russia',
    currencySymbol: 'RUB',
    currencyCode: '₽',
  },
  {
    dial: '+250',
    code_iso2: 'RW',
    title: 'Rwanda',
    currencySymbol: 'RWF',
    currencyCode: 'RWF',
  },
  {
    dial: '+381',
    code_iso2: 'RS',
    title: 'Serbia',
    currencySymbol: 'din.',
    currencyCode: 'RSD',
  },
  {
    dial: '+670',
    code_iso2: 'TL',
    title: 'East Timor',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+993',
    code_iso2: 'TM',
    title: 'Turkmenistan',
    currencySymbol: 'TMT',
    currencyCode: 'TMT',
  },
  {
    dial: '+992',
    code_iso2: 'TJ',
    title: 'Tajikistan',
    currencySymbol: 'TJS',
    currencyCode: 'TJS',
  },
  {
    dial: '+40',
    code_iso2: 'RO',
    title: 'Romania',
    currencySymbol: 'RON',
    currencyCode: 'RON',
    isEU: true,
  },
  {
    dial: '+690',
    code_iso2: 'TK',
    title: 'Tokelau',
    currencySymbol: 'NZ$',
    currencyCode: 'NZD',
  },
  {
    dial: '+245',
    code_iso2: 'GW',
    title: 'Guinea-Bissau',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+1671',
    code_iso2: 'GU',
    title: 'Guam',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+502',
    code_iso2: 'GT',
    title: 'Guatemala',
    currencySymbol: 'GTQ',
    currencyCode: 'GTQ',
  },
  {
    dial: '+30',
    code_iso2: 'GR',
    title: 'Greece',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+240',
    code_iso2: 'GQ',
    title: 'Equatorial Guinea',
    currencySymbol: 'FCFA',
    currencyCode: 'XAF',
  },
  {
    dial: '+81',
    code_iso2: 'JP',
    title: 'Japan',
    currencySymbol: '¥',
    currencyCode: 'JPY',
  },
  {
    dial: '+592',
    code_iso2: 'GY',
    title: 'Guyana',
    currencySymbol: 'GYD',
    currencyCode: 'GYD',
  },
  {
    dial: '+441481',
    code_iso2: 'GG',
    title: 'Guernsey',
    currencySymbol: '£',
    currencyCode: 'GBP',
  },
  {
    dial: '+594',
    code_iso2: 'GF',
    title: 'French Guiana',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+995',
    code_iso2: 'GE',
    title: 'Georgia',
    currencySymbol: 'GEL',
    currencyCode: 'GEL',
  },
  {
    dial: '+1473',
    code_iso2: 'GD',
    title: 'Grenada',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+44',
    code_iso2: 'GB',
    title: 'United Kingdom',
    currencySymbol: '£',
    currencyCode: 'GBP',
  },
  {
    dial: '+241',
    code_iso2: 'GA',
    title: 'Gabon',
    currencySymbol: 'FCFA',
    currencyCode: 'XAF',
  },
  {
    dial: '+503',
    code_iso2: 'SV',
    title: 'El Salvador',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+224',
    code_iso2: 'GN',
    title: 'Guinea',
    currencySymbol: 'FG',
    currencyCode: 'GNF',
  },
  {
    dial: '+220',
    code_iso2: 'GM',
    title: 'Gambia',
    currencySymbol: 'GMD',
    currencyCode: 'GMD',
  },
  {
    dial: '+299',
    code_iso2: 'GL',
    title: 'Greenland',
    currencySymbol: 'Dkr',
    currencyCode: 'DKK',
  },
  {
    dial: '+350',
    code_iso2: 'GI',
    title: 'Gibraltar',
    currencySymbol: 'GIP',
    currencyCode: 'GIP',
  },
  {
    dial: '+233',
    code_iso2: 'GH',
    title: 'Ghana',
    currencySymbol: 'GH₵',
    currencyCode: 'GHS',
  },
  {
    dial: '+968',
    code_iso2: 'OM',
    title: 'Oman',
    currencySymbol: 'OMR',
    currencyCode: 'OMR',
  },
  {
    dial: '+216',
    code_iso2: 'TN',
    title: 'Tunisia',
    currencySymbol: 'DT',
    currencyCode: 'TND',
  },
  {
    dial: '+962',
    code_iso2: 'JO',
    title: 'Jordan',
    currencySymbol: 'JD',
    currencyCode: 'JOD',
  },
  {
    dial: '+385',
    code_iso2: 'HR',
    title: 'Croatia',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+509',
    code_iso2: 'HT',
    title: 'Haiti',
    currencySymbol: 'HTG',
    currencyCode: 'HTG',
  },
  {
    dial: '+36',
    code_iso2: 'HU',
    title: 'Hungary',
    currencySymbol: 'Ft',
    currencyCode: 'HUF',
    isEU: true,
  },
  {
    dial: '+852',
    code_iso2: 'HK',
    title: 'Hong Kong',
    currencySymbol: 'HK$',
    currencyCode: 'HKD',
  },
  {
    dial: '+504',
    code_iso2: 'HN',
    title: 'Honduras',
    currencySymbol: 'HNL',
    currencyCode: 'HNL',
  },
  {
    dial: '+58',
    code_iso2: 'VE',
    title: 'Venezuela',
    currencySymbol: 'Bs.F.',
    currencyCode: 'VEF',
  },
  {
    dial: '+1787',
    code_iso2: 'PR',
    title: 'Puerto Rico',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+970',
    code_iso2: 'PS',
    title: 'Palestinian Territory',
    currencySymbol: '₪',
    currencyCode: 'ILS',
  },
  {
    dial: '+680',
    code_iso2: 'PW',
    title: 'Palau',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+351',
    code_iso2: 'PT',
    title: 'Portugal',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+47',
    code_iso2: 'SJ',
    title: 'Svalbard and Jan Mayen',
    currencySymbol: 'Nkr',
    currencyCode: 'NOK',
  },
  {
    dial: '+595',
    code_iso2: 'PY',
    title: 'Paraguay',
    currencySymbol: '₲',
    currencyCode: 'PYG',
  },
  {
    dial: '+964',
    code_iso2: 'IQ',
    title: 'Iraq',
    currencySymbol: 'IQD',
    currencyCode: 'IQD',
  },
  {
    dial: '+507',
    code_iso2: 'PA',
    title: 'Panama',
    currencySymbol: 'B/.',
    currencyCode: 'PAB',
  },
  {
    dial: '+689',
    code_iso2: 'PF',
    title: 'French Polynesia',
    currencySymbol: 'XPF',
    currencyCode: 'XPF',
  },
  {
    dial: '+675',
    code_iso2: 'PG',
    title: 'Papua New Guinea',
    currencySymbol: 'PGK',
    currencyCode: 'PGK',
  },
  {
    dial: '+51',
    code_iso2: 'PE',
    title: 'Peru',
    currencySymbol: 'S/.',
    currencyCode: 'PEN',
  },
  {
    dial: '+92',
    code_iso2: 'PK',
    title: 'Pakistan',
    currencySymbol: 'PKRs',
    currencyCode: 'PKR',
  },
  {
    dial: '+63',
    code_iso2: 'PH',
    title: 'Philippines',
    currencySymbol: '₱',
    currencyCode: 'PHP',
  },
  {
    dial: '+870',
    code_iso2: 'PN',
    title: 'Pitcairn',
    currencySymbol: 'NZ$',
    currencyCode: 'NZD',
  },
  {
    dial: '+48',
    code_iso2: 'PL',
    title: 'Poland',
    currencySymbol: 'zł',
    currencyCode: 'PLN',
    isEU: true,
  },
  {
    dial: '+260',
    code_iso2: 'ZM',
    title: 'Zambia',
    currencySymbol: 'ZK',
    currencyCode: 'ZMW',
  },
  {
    dial: '+212',
    code_iso2: 'EH',
    title: 'Western Sahara',
    currencySymbol: 'MAD',
    currencyCode: 'MAD',
  },
  {
    dial: '+372',
    code_iso2: 'EE',
    title: 'Estonia',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+20',
    code_iso2: 'EG',
    title: 'Egypt',
    currencySymbol: 'EGP',
    currencyCode: 'EGP',
  },
  {
    dial: '+27',
    code_iso2: 'ZA',
    title: 'South Africa',
    currencySymbol: 'R',
    currencyCode: 'ZAR',
  },
  {
    dial: '+593',
    code_iso2: 'EC',
    title: 'Ecuador',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+39',
    code_iso2: 'IT',
    title: 'Italy',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+84',
    code_iso2: 'VN',
    title: 'Vietnam',
    currencySymbol: '₫',
    currencyCode: 'VND',
  },
  {
    dial: '+677',
    code_iso2: 'SB',
    title: 'Solomon Islands',
    currencySymbol: 'SBD',
    currencyCode: 'SBD',
  },
  {
    dial: '+251',
    code_iso2: 'ET',
    title: 'Ethiopia',
    currencySymbol: 'Br',
    currencyCode: 'ETB',
  },
  {
    dial: '+263',
    code_iso2: 'ZW',
    title: 'Zimbabwe',
    currencySymbol: 'ZWL',
    currencyCode: 'ZWL',
  },
  {
    dial: '+966',
    code_iso2: 'SA',
    title: 'Saudi Arabia',
    currencySymbol: 'SR',
    currencyCode: 'SAR',
  },
  {
    dial: '+34',
    code_iso2: 'ES',
    title: 'Spain',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+291',
    code_iso2: 'ER',
    title: 'Eritrea',
    currencySymbol: 'Nfk',
    currencyCode: 'ERN',
  },
  {
    dial: '+382',
    code_iso2: 'ME',
    title: 'Montenegro',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+373',
    code_iso2: 'MD',
    title: 'Moldova',
    currencySymbol: 'MDL',
    currencyCode: 'MDL',
  },
  {
    dial: '+261',
    code_iso2: 'MG',
    title: 'Madagascar',
    currencySymbol: 'MGA',
    currencyCode: 'MGA',
  },
  {
    dial: '+212',
    code_iso2: 'MA',
    title: 'Morocco',
    currencySymbol: 'MAD',
    currencyCode: 'MAD',
  },
  {
    dial: '+377',
    code_iso2: 'MC',
    title: 'Monaco',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+998',
    code_iso2: 'UZ',
    title: 'Uzbekistan',
    currencySymbol: 'UZS',
    currencyCode: 'UZS',
  },
  {
    dial: '+95',
    code_iso2: 'MM',
    title: 'Myanmar',
    currencySymbol: 'MMK',
    currencyCode: 'MMK',
  },
  {
    dial: '+223',
    code_iso2: 'ML',
    title: 'Mali',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+853',
    code_iso2: 'MO',
    title: 'Macao',
    currencySymbol: 'MOP$',
    currencyCode: 'MOP',
  },
  {
    dial: '+976',
    code_iso2: 'MN',
    title: 'Mongolia',
    currencySymbol: 'MNT',
    currencyCode: 'MNT',
  },
  {
    dial: '+692',
    code_iso2: 'MH',
    title: 'Marshall Islands',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+389',
    code_iso2: 'MK',
    title: 'Macedonia',
    currencySymbol: 'MKD',
    currencyCode: 'MKD',
  },
  {
    dial: '+230',
    code_iso2: 'MU',
    title: 'Mauritius',
    currencySymbol: 'MURs',
    currencyCode: 'MUR',
  },
  {
    dial: '+356',
    code_iso2: 'MT',
    title: 'Malta',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+265',
    code_iso2: 'MW',
    title: 'Malawi',
    currencySymbol: 'MWK',
    currencyCode: 'MWK',
  },
  {
    dial: '+960',
    code_iso2: 'MV',
    title: 'Maldives',
    currencySymbol: 'MVR',
    currencyCode: 'MVR',
  },
  {
    dial: '+596',
    code_iso2: 'MQ',
    title: 'Martinique',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+1670',
    code_iso2: 'MP',
    title: 'Northern Mariana Islands',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+1664',
    code_iso2: 'MS',
    title: 'Montserrat',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+222',
    code_iso2: 'MR',
    title: 'Mauritania',
    currencySymbol: 'MRU',
    currencyCode: 'MRU',
  },
  {
    dial: '+44 1624',
    code_iso2: 'IM',
    title: 'Isle of Man',
    currencySymbol: '£',
    currencyCode: 'GBP',
  },
  {
    dial: '+256',
    code_iso2: 'UG',
    title: 'Uganda',
    currencySymbol: 'USh',
    currencyCode: 'UGX',
  },
  {
    dial: '+255',
    code_iso2: 'TZ',
    title: 'Tanzania',
    currencySymbol: 'TSh',
    currencyCode: 'TZS',
  },
  {
    dial: '+60',
    code_iso2: 'MY',
    title: 'Malaysia',
    currencySymbol: 'RM',
    currencyCode: 'MYR',
  },
  {
    dial: '+52',
    code_iso2: 'MX',
    title: 'Mexico',
    currencySymbol: 'MX$',
    currencyCode: 'MXN',
  },
  {
    dial: '+972',
    code_iso2: 'IL',
    title: 'Israel',
    currencySymbol: '₪',
    currencyCode: 'ILS',
  },
  {
    dial: '+33',
    code_iso2: 'FR',
    title: 'France',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+246',
    code_iso2: 'IO',
    title: 'British Indian Ocean Territory',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+358',
    code_iso2: 'FI',
    title: 'Finland',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+679',
    code_iso2: 'FJ',
    title: 'Fiji',
    currencySymbol: 'FJD',
    currencyCode: 'FJD',
  },
  {
    dial: '+500',
    code_iso2: 'FK',
    title: 'Falkland Islands',
    currencySymbol: 'FKP',
    currencyCode: 'FKP',
  },
  {
    dial: '+691',
    code_iso2: 'FM',
    title: 'Micronesia',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+298',
    code_iso2: 'FO',
    title: 'Faroe Islands',
    currencySymbol: 'Dkr',
    currencyCode: 'DKK',
  },
  {
    dial: '+505',
    code_iso2: 'NI',
    title: 'Nicaragua',
    currencySymbol: 'C$',
    currencyCode: 'NIO',
  },
  {
    dial: '+31',
    code_iso2: 'NL',
    title: 'Netherlands',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+47',
    code_iso2: 'NO',
    title: 'Norway',
    currencySymbol: 'Nkr',
    currencyCode: 'NOK',
  },
  {
    dial: '+264',
    code_iso2: 'NA',
    title: 'Namibia',
    currencySymbol: 'N$',
    currencyCode: 'NAD',
  },
  {
    dial: '+678',
    code_iso2: 'VU',
    title: 'Vanuatu',
    currencySymbol: 'VUV',
    currencyCode: 'VUV',
  },
  {
    dial: '+227',
    code_iso2: 'NE',
    title: 'Niger',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+672',
    code_iso2: 'NF',
    title: 'Norfolk Island',
    currencySymbol: 'AU$',
    currencyCode: 'AUD',
  },
  {
    dial: '+234',
    code_iso2: 'NG',
    title: 'Nigeria',
    currencySymbol: '₦',
    currencyCode: 'NGN',
  },
  {
    dial: '+64',
    code_iso2: 'NZ',
    title: 'New Zealand',
    currencySymbol: 'NZ$',
    currencyCode: 'NZD',
  },
  {
    dial: '+977',
    code_iso2: 'NP',
    title: 'Nepal',
    currencySymbol: 'NPRs',
    currencyCode: 'NPR',
  },
  {
    dial: '+674',
    code_iso2: 'NR',
    title: 'Nauru',
    currencySymbol: 'AU$',
    currencyCode: 'AUD',
  },
  {
    dial: '+683',
    code_iso2: 'NU',
    title: 'Niue',
    currencySymbol: 'NZ$',
    currencyCode: 'NZD',
  },
  {
    dial: '+682',
    code_iso2: 'CK',
    title: 'Cook Islands',
    currencySymbol: 'NZ$',
    currencyCode: 'NZD',
  },
  {
    dial: '+225',
    code_iso2: 'CI',
    title: 'Ivory Coast',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+41',
    code_iso2: 'CH',
    title: 'Switzerland',
    currencySymbol: 'CHF',
    currencyCode: 'CHF',
  },
  {
    dial: '+57',
    code_iso2: 'CO',
    title: 'Colombia',
    currencySymbol: 'CO$',
    currencyCode: 'COP',
  },
  {
    dial: '+86',
    code_iso2: 'CN',
    title: 'China',
    currencySymbol: 'CN¥',
    currencyCode: 'CNY',
  },
  {
    dial: '+237',
    code_iso2: 'CM',
    title: 'Cameroon',
    currencySymbol: 'FCFA',
    currencyCode: 'XAF',
  },
  {
    dial: '+56',
    code_iso2: 'CL',
    title: 'Chile',
    currencySymbol: 'CL$',
    currencyCode: 'CLP',
  },
  {
    dial: '+61',
    code_iso2: 'CC',
    title: 'Cocos Islands',
    currencySymbol: 'AU$',
    currencyCode: 'AUD',
  },
  {
    dial: '+1',
    code_iso2: 'US',
    title: 'United States',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+1',
    code_iso2: 'CA',
    title: 'Canada',
    currencySymbol: 'CA$',
    currencyCode: 'CAD',
  },
  {
    dial: '+242',
    code_iso2: 'CG',
    title: 'Republic of the Congo',
    currencySymbol: 'FCFA',
    currencyCode: 'XAF',
  },
  {
    dial: '+236',
    code_iso2: 'CF',
    title: 'Central African Republic',
    currencySymbol: 'FCFA',
    currencyCode: 'XAF',
  },
  {
    dial: '+243',
    code_iso2: 'CD',
    title: 'Democratic Republic of the Congo',
    currencySymbol: 'CDF',
    currencyCode: 'CDF',
  },
  {
    dial: '+420',
    code_iso2: 'CZ',
    title: 'Czech Republic',
    currencySymbol: 'Kč',
    currencyCode: 'CZK',
    isEU: true,
  },
  {
    dial: '+357',
    code_iso2: 'CY',
    title: 'Cyprus',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+61',
    code_iso2: 'CX',
    title: 'Christmas Island',
    currencySymbol: 'AU$',
    currencyCode: 'AUD',
  },
  {
    dial: '+506',
    code_iso2: 'CR',
    title: 'Costa Rica',
    currencySymbol: '₡',
    currencyCode: 'CRC',
  },
  {
    dial: '+599',
    code_iso2: 'CW',
    title: 'Curacao',
    currencySymbol: 'ANG',
    currencyCode: 'ANG',
  },
  {
    dial: '+53',
    code_iso2: 'CU',
    title: 'Cuba',
    currencySymbol: 'CUP',
    currencyCode: 'CUP',
  },
  {
    dial: '+268',
    code_iso2: 'SZ',
    title: 'Swaziland',
    currencySymbol: 'SZL',
    currencyCode: 'SZL',
  },
  {
    dial: '+963',
    code_iso2: 'SY',
    title: 'Syria',
    currencySymbol: 'SY£',
    currencyCode: 'SYP',
  },
  {
    dial: '+599',
    code_iso2: 'SX',
    title: 'Sint Maarten',
    currencySymbol: 'ANG',
    currencyCode: 'ANG',
  },
  {
    dial: '+996',
    code_iso2: 'KG',
    title: 'Kyrgyzstan',
    currencySymbol: 'KGS',
    currencyCode: 'KGS',
  },
  {
    dial: '+254',
    code_iso2: 'KE',
    title: 'Kenya',
    currencySymbol: 'Ksh',
    currencyCode: 'KES',
  },
  {
    dial: '+211',
    code_iso2: 'SS',
    title: 'South Sudan',
    currencySymbol: 'SSP',
    currencyCode: 'SSP',
  },
  {
    dial: '+252',
    code_iso2: 'SO',
    title: 'Somalia',
    currencySymbol: 'SOS',
    currencyCode: 'SOS',
  },
  {
    dial: '+597',
    code_iso2: 'SR',
    title: 'Suriname',
    currencySymbol: 'SRD',
    currencyCode: 'SRD',
  },
  {
    dial: '+686',
    code_iso2: 'KI',
    title: 'Kiribati',
    currencySymbol: 'AU$',
    currencyCode: 'AUD',
  },
  {
    dial: '+855',
    code_iso2: 'KH',
    title: 'Cambodia',
    currencySymbol: 'KHR',
    currencyCode: 'KHR',
  },
  {
    dial: '+1869',
    code_iso2: 'KN',
    title: 'Saint Kitts and Nevis',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+269',
    code_iso2: 'KM',
    title: 'Comoros',
    currencySymbol: 'CF',
    currencyCode: 'KMF',
  },
  {
    dial: '+239',
    code_iso2: 'ST',
    title: 'Sao Tome and Principe',
    currencySymbol: 'STD',
    currencyCode: 'STD',
  },
  {
    dial: '+421',
    code_iso2: 'SK',
    title: 'Slovakia',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+82',
    code_iso2: 'KR',
    title: 'South Korea',
    currencySymbol: '₩',
    currencyCode: 'KRW',
  },
  {
    dial: '+386',
    code_iso2: 'SI',
    title: 'Slovenia',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+850',
    code_iso2: 'KP',
    title: 'North Korea',
    currencySymbol: 'KPW',
    currencyCode: 'KPW',
  },
  {
    dial: '+965',
    code_iso2: 'KW',
    title: 'Kuwait',
    currencySymbol: 'KD',
    currencyCode: 'KWD',
  },
  {
    dial: '+221',
    code_iso2: 'SN',
    title: 'Senegal',
    currencySymbol: 'CFA',
    currencyCode: 'XOF',
  },
  {
    dial: '+378',
    code_iso2: 'SM',
    title: 'San Marino',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+232',
    code_iso2: 'SL',
    title: 'Sierra Leone',
    currencySymbol: 'SLL',
    currencyCode: 'SLL',
  },
  {
    dial: '+248',
    code_iso2: 'SC',
    title: 'Seychelles',
    currencySymbol: 'SCR',
    currencyCode: 'SCR',
  },
  {
    dial: '+7',
    code_iso2: 'KZ',
    title: 'Kazakhstan',
    currencySymbol: 'KZT',
    currencyCode: 'KZT',
  },
  {
    dial: '+1345',
    code_iso2: 'KY',
    title: 'Cayman Islands',
    currencySymbol: 'KYD',
    currencyCode: 'KYD',
  },
  {
    dial: '+65',
    code_iso2: 'SG',
    title: 'Singapore',
    currencySymbol: 'S$',
    currencyCode: 'SGD',
  },
  {
    dial: '+46',
    code_iso2: 'SE',
    title: 'Sweden',
    currencySymbol: 'Skr',
    currencyCode: 'SEK',
    isEU: true,
  },
  {
    dial: '+249',
    code_iso2: 'SD',
    title: 'Sudan',
    currencySymbol: 'SDG',
    currencyCode: 'SDG',
  },
  {
    dial: '+1849',
    code_iso2: 'DO',
    title: 'Dominican Republic',
    currencySymbol: 'RD$',
    currencyCode: 'DOP',
  },
  {
    dial: '+1829',
    code_iso2: 'DO',
    title: 'Dominican Republic',
    currencySymbol: 'RD$',
    currencyCode: 'DOP',
  },
  {
    dial: '+1809',
    code_iso2: 'DO',
    title: 'Dominican Republic',
    currencySymbol: 'RD$',
    currencyCode: 'DOP',
  },
  {
    dial: '+1767',
    code_iso2: 'DM',
    title: 'Dominica',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+253',
    code_iso2: 'DJ',
    title: 'Djibouti',
    currencySymbol: 'Fdj',
    currencyCode: 'DJF',
  },
  {
    dial: '+45',
    code_iso2: 'DK',
    title: 'Denmark',
    currencySymbol: 'Dkr',
    currencyCode: 'DKK',
    isEU: true,
  },
  {
    dial: '+1284',
    code_iso2: 'VG',
    title: 'British Virgin Islands',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+49',
    code_iso2: 'DE',
    title: 'Germany',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+967',
    code_iso2: 'YE',
    title: 'Yemen',
    currencySymbol: 'YR',
    currencyCode: 'YER',
  },
  {
    dial: '+213',
    code_iso2: 'DZ',
    title: 'Algeria',
    currencySymbol: 'DA',
    currencyCode: 'DZD',
  },
  {
    dial: '+598',
    code_iso2: 'UY',
    title: 'Uruguay',
    currencySymbol: '$U',
    currencyCode: 'UYU',
  },
  {
    dial: '+961',
    code_iso2: 'LB',
    title: 'Lebanon',
    currencySymbol: 'LB£',
    currencyCode: 'LBP',
  },
  {
    dial: '+1758',
    code_iso2: 'LC',
    title: 'Saint Lucia',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+856',
    code_iso2: 'LA',
    title: 'Laos',
    currencySymbol: 'LAK',
    currencyCode: 'LAK',
  },
  {
    dial: '+688',
    code_iso2: 'TV',
    title: 'Tuvalu',
    currencySymbol: 'AU$',
    currencyCode: 'AUD',
  },
  {
    dial: '+886',
    code_iso2: 'TW',
    title: 'Taiwan',
    currencySymbol: 'NT$',
    currencyCode: 'TWD',
  },
  {
    dial: '+1868',
    code_iso2: 'TT',
    title: 'Trinidad and Tobago',
    currencySymbol: 'TT$',
    currencyCode: 'TTD',
  },
  {
    dial: '+90',
    code_iso2: 'TR',
    title: 'Turkey',
    currencySymbol: '₺',
    currencyCode: 'TRY',
  },
  {
    dial: '+94',
    code_iso2: 'LK',
    title: 'Sri Lanka',
    currencySymbol: 'SLRs',
    currencyCode: 'LKR',
  },
  {
    dial: '+423',
    code_iso2: 'LI',
    title: 'Liechtenstein',
    currencySymbol: 'CHF',
    currencyCode: 'CHF',
  },
  {
    dial: '+371',
    code_iso2: 'LV',
    title: 'Latvia',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+676',
    code_iso2: 'TO',
    title: 'Tonga',
    currencySymbol: 'T$',
    currencyCode: 'TOP',
  },
  {
    dial: '+370',
    code_iso2: 'LT',
    title: 'Lithuania',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+352',
    code_iso2: 'LU',
    title: 'Luxembourg',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+231',
    code_iso2: 'LR',
    title: 'Liberia',
    currencySymbol: 'LRD',
    currencyCode: 'LRD',
  },
  {
    dial: '+266',
    code_iso2: 'LS',
    title: 'Lesotho',
    currencySymbol: 'LSL',
    currencyCode: 'LSL',
  },
  {
    dial: '+66',
    code_iso2: 'TH',
    title: 'Thailand',
    currencySymbol: '฿',
    currencyCode: 'THB',
  },
  {
    dial: '+235',
    code_iso2: 'TD',
    title: 'Chad',
    currencySymbol: 'FCFA',
    currencyCode: 'XAF',
  },
  {
    dial: '+1649',
    code_iso2: 'TC',
    title: 'Turks and Caicos Islands',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+218',
    code_iso2: 'LY',
    title: 'Libya',
    currencySymbol: 'LD',
    currencyCode: 'LYD',
  },
  {
    dial: '+379',
    code_iso2: 'VA',
    title: 'Vatican',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+1784',
    code_iso2: 'VC',
    title: 'Saint Vincent and the Grenadines',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+971',
    code_iso2: 'AE',
    title: 'United Arab Emirates',
    currencySymbol: 'AED',
    currencyCode: 'AED',
  },
  {
    dial: '+376',
    code_iso2: 'AD',
    title: 'Andorra',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+1268',
    code_iso2: 'AG',
    title: 'Antigua and Barbuda',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+93',
    code_iso2: 'AF',
    title: 'Afghanistan',
    currencySymbol: 'Af',
    currencyCode: 'AFN',
  },
  {
    dial: '+1264',
    code_iso2: 'AI',
    title: 'Anguilla',
    currencySymbol: 'XCD',
    currencyCode: 'XCD',
  },
  {
    dial: '+1340',
    code_iso2: 'VI',
    title: 'U.S. Virgin Islands',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+354',
    code_iso2: 'IS',
    title: 'Iceland',
    currencySymbol: 'Ikr',
    currencyCode: 'ISK',
  },
  {
    dial: '+98',
    code_iso2: 'IR',
    title: 'Iran',
    currencySymbol: 'IRR',
    currencyCode: 'IRR',
  },
  {
    dial: '+374',
    code_iso2: 'AM',
    title: 'Armenia',
    currencySymbol: 'AMD',
    currencyCode: 'AMD',
  },
  {
    dial: '+244',
    code_iso2: 'AO',
    title: 'Angola',
    currencySymbol: 'AOA',
    currencyCode: 'AOA',
  },
  {
    dial: '+1684',
    code_iso2: 'AS',
    title: 'American Samoa',
    currencySymbol: '$',
    currencyCode: 'USD',
  },
  {
    dial: '+54',
    code_iso2: 'AR',
    title: 'Argentina',
    currencySymbol: 'AR$',
    currencyCode: 'ARS',
  },
  {
    dial: '+61',
    code_iso2: 'AU',
    title: 'Australia',
    currencySymbol: '$',
    currencyCode: 'AUD',
  },
  {
    dial: '+43',
    code_iso2: 'AT',
    title: 'Austria',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+297',
    code_iso2: 'AW',
    title: 'Aruba',
    currencySymbol: 'AWG',
    currencyCode: 'AWG',
  },
  {
    dial: '+91',
    code_iso2: 'IN',
    title: 'India',
    currencySymbol: '₹',
    currencyCode: 'INR',
  },
  {
    dial: '+35818',
    code_iso2: 'AX',
    title: 'Aland Islands',
    currencySymbol: '€',
    currencyCode: 'EUR',
  },
  {
    dial: '+994',
    code_iso2: 'AZ',
    title: 'Azerbaijan',
    currencySymbol: 'man.',
    currencyCode: 'AZN',
  },
  {
    dial: '+353',
    code_iso2: 'IE',
    title: 'Ireland',
    currencySymbol: '€',
    currencyCode: 'EUR',
    isEU: true,
  },
  {
    dial: '+62',
    code_iso2: 'ID',
    title: 'Indonesia',
    currencySymbol: 'Rp',
    currencyCode: 'IDR',
  },
  {
    dial: '+380',
    code_iso2: 'UA',
    title: 'Ukraine',
    currencySymbol: '₴',
    currencyCode: 'UAH',
  },
  {
    dial: '+974',
    code_iso2: 'QA',
    title: 'Qatar',
    currencySymbol: 'QR',
    currencyCode: 'QAR',
  },
  {
    dial: '+258',
    code_iso2: 'MZ',
    title: 'Mozambique',
    currencySymbol: 'MTn',
    currencyCode: 'MZN',
  },
];

export const usStates = [
  {
    name: 'Alabama',
    code: 'AL',
  },
  {
    name: 'Alaska',
    code: 'AK',
  },
  {
    name: 'American Samoa',
    code: 'AS',
  },
  {
    name: 'Arizona',
    code: 'AZ',
  },
  {
    name: 'Arkansas',
    code: 'AR',
  },
  {
    name: 'California',
    code: 'CA',
  },
  {
    name: 'Colorado',
    code: 'CO',
  },
  {
    name: 'Connecticut',
    code: 'CT',
  },
  {
    name: 'Delaware',
    code: 'DE',
  },
  {
    name: 'District Of Columbia',
    code: 'DC',
  },
  {
    name: 'Federated States Of Micronesia',
    code: 'FM',
  },
  {
    name: 'Florida',
    code: 'FL',
  },
  {
    name: 'Georgia',
    code: 'GA',
  },
  {
    name: 'Guam',
    code: 'GU',
  },
  {
    name: 'Hawaii',
    code: 'HI',
  },
  {
    name: 'Idaho',
    code: 'ID',
  },
  {
    name: 'Illinois',
    code: 'IL',
  },
  {
    name: 'Indiana',
    code: 'IN',
  },
  {
    name: 'Iowa',
    code: 'IA',
  },
  {
    name: 'Kansas',
    code: 'KS',
  },
  {
    name: 'Kentucky',
    code: 'KY',
  },
  {
    name: 'Louisiana',
    code: 'LA',
  },
  {
    name: 'Maine',
    code: 'ME',
  },
  {
    name: 'Marshall Islands',
    code: 'MH',
  },
  {
    name: 'Maryland',
    code: 'MD',
  },
  {
    name: 'Massachusetts',
    code: 'MA',
  },
  {
    name: 'Michigan',
    code: 'MI',
  },
  {
    name: 'Minnesota',
    code: 'MN',
  },
  {
    name: 'Mississippi',
    code: 'MS',
  },
  {
    name: 'Missouri',
    code: 'MO',
  },
  {
    name: 'Montana',
    code: 'MT',
  },
  {
    name: 'Nebraska',
    code: 'NE',
  },
  {
    name: 'Nevada',
    code: 'NV',
  },
  {
    name: 'New Hampshire',
    code: 'NH',
  },
  {
    name: 'New Jersey',
    code: 'NJ',
  },
  {
    name: 'New Mexico',
    code: 'NM',
  },
  {
    name: 'New York',
    code: 'NY',
  },
  {
    name: 'North Carolina',
    code: 'NC',
  },
  {
    name: 'North Dakota',
    code: 'ND',
  },
  {
    name: 'Northern Mariana Islands',
    code: 'MP',
  },
  {
    name: 'Ohio',
    code: 'OH',
  },
  {
    name: 'Oklahoma',
    code: 'OK',
  },
  {
    name: 'Oregon',
    code: 'OR',
  },
  {
    name: 'Palau',
    code: 'PW',
  },
  {
    name: 'Pennsylvania',
    code: 'PA',
  },
  {
    name: 'Puerto Rico',
    code: 'PR',
  },
  {
    name: 'Rhode Island',
    code: 'RI',
  },
  {
    name: 'South Carolina',
    code: 'SC',
  },
  {
    name: 'South Dakota',
    code: 'SD',
  },
  {
    name: 'Tennessee',
    code: 'TN',
  },
  {
    name: 'Texas',
    code: 'TX',
  },
  {
    name: 'Utah',
    code: 'UT',
  },
  {
    name: 'Vermont',
    code: 'VT',
  },
  {
    name: 'Virgin Islands',
    code: 'VI',
  },
  {
    name: 'Virginia',
    code: 'VA',
  },
  {
    name: 'Washington',
    code: 'WA',
  },
  {
    name: 'West Virginia',
    code: 'WV',
  },
  {
    name: 'Wisconsin',
    code: 'WI',
  },
  {
    name: 'Wyoming',
    code: 'WY',
  },
];

export const canadaStates = [
  {
    name: 'Alberta',
    code: 'AB',
    areaCodes: [368, 403, 587, 780, 825],
  },
  {
    name: 'British Columbia',
    code: 'BC',
    areaCodes: [236, 250, 604, 672, 778],
  },
  {
    name: 'Manitoba',
    code: 'MB',
    areaCodes: [204, 431],
  },
  {
    name: 'New Brunswick',
    code: 'NB',
    areaCodes: [506, 428],
  },
  {
    name: 'Newfoundland and Labrador',
    code: 'NL',
    areaCodes: [709, 879],
  },
  {
    name: 'Northwest Territories',
    code: 'NT',
    areaCodes: [867],
  },
  {
    name: 'Nova Scotia',
    code: 'NS',
    areaCodes: [782, 902],
  },
  {
    name: 'Nunavut',
    code: 'NU',
    areaCodes: [867],
  },
  {
    name: 'Ontario',
    code: 'ON',
    areaCodes: [226, 249, 289, 343, 365, 416, 437, 519, 548, 613, 647, 705, 807, 905],
  },
  {
    name: 'Prince Edward Island',
    code: 'PE',
    areaCodes: [782, 902],
  },
  {
    name: 'Quebec',
    code: 'QC',
    areaCodes: [354, 367, 418, 438, 450, 514, 579, 581, 819, 873],
  },
  {
    name: 'Saskatchewan',
    code: 'SK',
    areaCodes: [306, 474, 639],
  },
  {
    name: 'Yukon Territory',
    code: 'YT',
    areaCodes: [867, 600, 622],
  },
];
